import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import EventCardDetailed from "../../components/common/EventCard/EventCardDetailed";
import { getAllOrganizerEvents, getAllEventsByOrganizerId, activateEventBySlug, getAllCategories } from "../../api";
import EventNotFound from "../../components/common/EventCard/EventNotFound";
import { EventType } from "../../types/Events";
import PulseLoader from "react-spinners/PulseLoader";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Background from "../Login/Background";
import { useNightMode } from "../../contexts/NightModeContext";
import { Search, Plus } from "lucide-react";

import CustomSelect from "../../components/common/CustomSelect/CustomSelect";
import PageHeader from "../../components/common/PageHeader/PageHeader";

/** Category dropdown option — filter by id because events only expose categoryId. */
type CategoryOption = { id: string; name: string };

const Events: React.FC = () => {
  const navigate = useNavigate();
  const { isNightMode } = useNightMode();
  const [eventList, setEventList] = useState<EventType[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("All");
  const [dateFilter, setDateFilter] = useState<string>("All");
  const [eventStatus, setEventStatus] = useState<"All" | "Live" | "Draft" | "Completed" | "Cancelled">("All");
  const [loading, setLoading] = useState<boolean>(true);
  const [isRole2, setIsRole2] = useState<boolean>(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    event: EventType;
    action: "publish" | "unpublish";
  } | null>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await getAllCategories();
        if (Array.isArray(res)) {
          const list: CategoryOption[] = res
            .map((c: any) => ({
              id: String(c.categoryId ?? c.id ?? "").trim(),
              name: String(c.categoryName || c.name || "").trim(),
            }))
            .filter((c) => c.id && c.name);
          // Dedupe by id
          const byId = new Map<string, CategoryOption>();
          list.forEach((c) => byId.set(c.id, c));
          setCategories(Array.from(byId.values()));
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };

    const fetchEvents = async () => {
      try {
        setLoading(true);
        let response: any = [];
        let isEventSecondAdmin = false;
        let organizerId = null;
        let userEventIds: any[] = [];
        let userRole = "";

        try {
          const authUser = localStorage.getItem("authUser");
          if (authUser) {
            const parsedData = JSON.parse(authUser);
            userRole = String(parsedData.user?.userRole ?? "");
            isEventSecondAdmin = userRole === "8" || userRole === "10";
            setIsRole2(userRole === "2");
            organizerId = parsedData.user?.organizerId;
            userEventIds = parsedData.user?.eventId || [];
          }
        } catch (err) {
          console.error("Error parsing authUser", err);
        }

        if (isEventSecondAdmin && organizerId) {
          const res = await getAllEventsByOrganizerId(organizerId);
          response = res?.events || [];
          localStorage.setItem("organizerData", JSON.stringify(res));
          if (userRole === "8") {
            response = response.filter((event: any) =>
              userEventIds.map(String).includes(String(event.eventId))
            );
          }
        } else {
          const res = await getAllOrganizerEvents();
          response = res || [];
        }

        const now = new Date();
        const futureEvents = response.filter(
          (event: EventType) => new Date(event.eventDate) >= now
        );
        const pastEvents = response.filter(
          (event: EventType) => new Date(event.eventDate) < now
        );
        const sortedFuture = [...futureEvents].sort(
          (a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime()
        );
        const sortedPast = [...pastEvents].sort(
          (a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime()
        );

        setEventList([...sortedFuture, ...sortedPast]);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
    fetchEvents();
  }, []);

  // id → name so cards can show the label (events only have categoryId from API)
  const categoryNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((c) => map.set(c.id, c.name));
    return map;
  }, [categories]);

  const resolveCategoryName = (event: EventType) => {
    const existing = (event as any).categoryName || (event as any).category || "";
    if (existing) return String(existing);
    const id = event.categoryId != null ? String(event.categoryId) : "";
    return id ? categoryNameById.get(id) || "" : "";
  };

  const handleStatusToggle = async () => {
    if (!confirmDialog) return;
    const { event, action } = confirmDialog;
    const isActivating = action === "publish";

    try {
      const slug = event.slug || event.eventSlug;
      if (slug) {
        await activateEventBySlug(slug);
      }
      setEventList(prev =>
        prev.map(item =>
          item.eventId === event.eventId
            ? { ...item, eventStatus: isActivating ? "active" : "draft" }
            : item
        )
      );
      toast.success(`Event successfully ${isActivating ? "published" : "unpublished"}!`);
    } catch (err) {
      console.error(err);
      toast.error(`Failed to ${action} event.`);
    } finally {
      setConfirmDialog(null);
    }
  };

  const filteredEvents = eventList.filter(event => {
    // Search query
    const matchesSearch =
      search.trim() === "" ||
      event.eventName?.toLowerCase().includes(search.toLowerCase()) ||
      event.eventVenue?.toLowerCase().includes(search.toLowerCase()) ||
      event.eventCity?.toLowerCase().includes(search.toLowerCase()) ||
      event.shortDescription?.toLowerCase().includes(search.toLowerCase());

    // Category filter — compare ids (dropdown value is categoryId)
    const matchesCategory =
      categoryFilter === "All" ||
      String(event.categoryId ?? "") === String(categoryFilter);

    // Date filter
    let matchesDate = true;
    const evtDate = new Date(event.eventDate);
    const now = new Date();
    if (dateFilter === "Upcoming") {
      matchesDate = evtDate >= now;
    } else if (dateFilter === "Past") {
      matchesDate = evtDate < now;
    }

    // Status tab filter
    let matchesStatus = true;
    const statusLower = (event.eventStatus || (event as any).status || "").toLowerCase();
    if (eventStatus === "Live") {
      matchesStatus = statusLower === "active" || statusLower === "live" || statusLower === "published";
    } else if (eventStatus === "Draft") {
      matchesStatus = statusLower === "draft";
    } else if (eventStatus === "Completed") {
      matchesStatus = statusLower === "past" || statusLower === "completed" || statusLower === "closed";
    } else if (eventStatus === "Cancelled") {
      matchesStatus = statusLower === "cancelled";
    }

    return matchesSearch && matchesCategory && matchesDate && matchesStatus;
  });

  const categoryOptions = [
    { label: "All Categories", value: "All" },
    ...categories.map((c) => ({ label: c.name, value: c.id })),
  ];

  const dateOptions = [
    { label: "Any Date", value: "All" },
    { label: "Upcoming Events", value: "Upcoming" },
    { label: "Past Events", value: "Past" }
  ];

  return (
    <div className={`eventox-events-page position-relative ${isNightMode ? 'text-white' : 'text-dark'}`}>
      <Background />

      <div className="position-relative py-3 d-flex flex-column gap-4" style={{ zIndex: 10, maxWidth: 1440, margin: "0 auto", width: "100%" }}>
        
        {/* Header Rail */}
        <PageHeader
          title="Events Directory"
          subtitle="Manage your ticketing channels, adjust live pricing structures, and track physical capacities."
          isLoading={loading}
          actions={
            isRole2 ? (
              <button
                type="button"
                className="btn-create-event"
                onClick={() => navigate("/create-quick-event-v2")}
              >
                <Plus size={16} /> Create Event
              </button>
            ) : undefined
          }
        />

        {/* Filter Rail */}
        <div className="events-filter-rail d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
          {/* Search Box */}
          <div className="events-search-wrap">
            <Search size={15} className="events-search-icon" />
            <input
              type="text"
              placeholder="Search events..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Category Filter */}
          <div className="events-select-wrap">
            <CustomSelect
              options={categoryOptions}
              value={categoryFilter}
              onChange={(val: string) => setCategoryFilter(val)}
              placeholder="All Categories"
            />
          </div>

          {/* Date Filter */}
          <div className="events-select-wrap">
            <CustomSelect
              options={dateOptions}
              value={dateFilter}
              onChange={(val: string) => setDateFilter(val)}
              placeholder="Any Date"
            />
          </div>

          {/* Status Tabs */}
          <div className="events-status-tabs">
            {(["All", "Live", "Draft", "Completed", "Cancelled"] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                className={`events-tab-btn ${eventStatus === tab ? "active" : ""}`}
                onClick={() => setEventStatus(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Content Area */}
        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <PulseLoader color="#EE3F7C" size={14} />
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="py-5">
            <EventNotFound />
          </div>
        ) : (
          <div className="events-grid-container mt-2">
            {filteredEvents.map((evt, idx) => (
              <EventCardDetailed
                key={evt.eventId}
                evt={{
                  ...evt,
                  // Cards read categoryName; organizer list only returns categoryId
                  categoryName: resolveCategoryName(evt),
                } as EventType}
                idx={idx}
                onSelectEvent={(e) => {
                  localStorage.setItem("eventData", JSON.stringify(e));
                  sessionStorage.setItem("eventNavSource", "/events");
                  const status = (e.eventStatus || e.status || "").toLowerCase();
                  if (status === "draft") {
                    navigate(`/events/${e.eventId}/preview`, { state: { from: "/events" } });
                  } else {
                    navigate(`/event/${e.eventId}`, { state: { from: "/events" } });
                  }
                }}
                onEdit={(e) => {
                  localStorage.setItem("eventData", JSON.stringify(e));
                  sessionStorage.setItem("eventNavSource", "/events");
                  navigate(`/event/${e.eventId}/edit-event`, { state: { from: "/events" } });
                }}
                onPublish={(e) => {
                  setConfirmDialog({ event: e, action: "publish" });
                }}
                onUnpublish={(e) => {
                  setConfirmDialog({ event: e, action: "unpublish" });
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmDialog && (
        <div className="confirm-overlay d-flex align-items-center justify-content-center p-3">
          <div className="confirm-dialog">
            <div className="confirm-dialog-top-bar" />
            <div className="d-flex align-items-start gap-3 mb-3">
              <div
                className={`confirm-icon-wrap d-flex align-items-center justify-content-center ${
                  confirmDialog.action === "publish" ? "confirm-icon-publish" : "confirm-icon-unpublish"
                }`}
              >
                <i
                  className={`fa-solid ${
                    confirmDialog.action === "publish" ? "fa-globe text-green" : "fa-eye-slash text-pink"
                  }`}
                />
              </div>
              <div>
                <h5 className="text-white font-jakarta fw-bold mb-1 fs-16px">
                  {confirmDialog.action === "publish" ? "Publish Event?" : "Unpublish Event?"}
                </h5>
                <p className="text-white-50 fs-12px mb-0">
                  {confirmDialog.action === "publish"
                    ? `This will make "${confirmDialog.event.eventName}" visible to all users and open ticket sales.`
                    : `This will revert "${confirmDialog.event.eventName}" back to Draft mode.`}
                </p>
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-end gap-2 mt-4 pt-2 border-top border-white border-opacity-10">
              <button
                type="button"
                className="confirm-btn-cancel"
                onClick={() => setConfirmDialog(null)}
              >
                CANCEL
              </button>
              <button
                type="button"
                className={`confirm-btn-confirm ${
                  confirmDialog.action === "publish" ? "confirm-btn-publish" : "confirm-btn-unpublish"
                }`}
                onClick={handleStatusToggle}
              >
                {confirmDialog.action === "publish" ? "PUBLISH NOW" : "UNPUBLISH"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} theme={isNightMode ? "dark" : "light"} />
    </div>
  );
};

export default Events;
